import { FormProvider } from "react-hook-form";
import { classNames } from "primereact/utils";
import { startOfToday } from "date-fns";
import { AppDialog } from "../../../AppDialog/AppDialog";
import { DefaultButton } from "../../../button/defaultButton/DefaultButton";
import { DefaultInputField } from "../../../forms/inputs/InputField/DefaultInputField";
import { InputDateField } from "../../../forms/inputs/inputDateField/InputDateField";
import { InputError } from "../../../forms/inputs/inputError/InputError";
import { RenderWhen } from "../../../../infrastructure/components/RenderWhen";
import { BodyMetricType } from "../../../../enums";
import { BodyMetricTypeLabels } from "../../../../labels";
import { BodyMetricIcon } from "../../BodyMetricIcon/BodyMetricIcon";
import { IBodyMetricsDataSource } from "../../models/bodyMetricModels";
import {
  BODY_METRIC_GROUPS,
  BODY_METRIC_TYPES,
  BODY_METRIC_TYPE_ORDER,
} from "../../models/bodyMetricTypes";
import { useAddBodyMetricDialog } from "../../hooks/useAddBodyMetricDialog";
import styles from "./AddBodyMetricDialog.module.scss";

interface IAddBodyMetricDialogProps {
  visible: boolean;
  presetType: BodyMetricType | null;
  dataSource: IBodyMetricsDataSource;
  onHide: () => void;
}

export const AddBodyMetricDialog = ({
  visible,
  presetType,
  dataSource,
  onHide,
}: IAddBodyMetricDialogProps) => {
  const { form, errorMessage, selectedType, config, selectType, isLoading, handleSubmit } =
    useAddBodyMetricDialog({ visible, presetType, dataSource, onHide });

  return (
    <AppDialog
      header={selectedType ? "Нова стойност" : "Какво ще измервате?"}
      visible={visible}
      onHide={onHide}
      className={styles.root}
      dismissableMask={false}
    >
      <RenderWhen condition={!selectedType}>
        <div className={styles.root_groups}>
          {BODY_METRIC_GROUPS.map(({ group, label }) => (
            <section key={group} className={styles.root_group}>
              <h3 className={styles.root_group_title}>{label}</h3>
              <div className={styles.root_group_grid}>
                {BODY_METRIC_TYPE_ORDER.filter((type) => BODY_METRIC_TYPES[type].group === group).map(
                  (type) => (
                    <button
                      key={type}
                      type="button"
                      className={classNames(styles.root_tile, styles[`root_tile_${group}`])}
                      onClick={() => selectType(type)}
                    >
                      <span className={styles.root_tile_orb}>
                        <BodyMetricIcon type={type} size={22} />
                      </span>
                      <span className={styles.root_tile_label}>{BodyMetricTypeLabels[type]}</span>
                      <span className={styles.root_tile_unit}>{BODY_METRIC_TYPES[type].unit}</span>
                    </button>
                  ),
                )}
              </div>
            </section>
          ))}
        </div>
      </RenderWhen>

      <RenderWhen condition={!!selectedType && !!config}>
        <FormProvider {...form}>
          {/* noValidate: the yup messages explain a bad value, not the browser's tooltip. */}
          <form className={styles.root_form} onSubmit={handleSubmit} noValidate>
            <div className={classNames(styles.root_selected, styles[`root_tile_${config?.group}`])}>
              <span className={styles.root_tile_orb}>
                <BodyMetricIcon type={selectedType!} size={22} />
              </span>
              <span className={styles.root_selected_label}>
                {selectedType ? BodyMetricTypeLabels[selectedType] : ""}
              </span>
              <RenderWhen condition={!presetType}>
                <button
                  type="button"
                  className={styles.root_selected_change}
                  onClick={() => selectType(null)}
                >
                  Смени
                </button>
              </RenderWhen>
            </div>

            <DefaultInputField
              name="value"
              label={`Стойност (${config?.unit ?? ""})`}
              placeholder={`${config?.min ?? ""} – ${config?.max ?? ""}`}
              type="number"
              min={config?.min}
              max={config?.max}
              step={10 ** -(config?.decimals ?? 2)}
              autofocus
              required
              width="100%"
            />

            <InputDateField
              name="measuredOn"
              label="Дата на измерване"
              maxDate={startOfToday()}
              required
              width="100%"
            />

            <RenderWhen condition={!!errorMessage}>
              <InputError message={errorMessage} />
            </RenderWhen>

            <div className={styles.root_footer}>
              <DefaultButton label="Запази" type="submit" loading={isLoading} width="100%" />
            </div>
          </form>
        </FormProvider>
      </RenderWhen>
    </AppDialog>
  );
};
